import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePopupBlocked } from '../contexts/PopupBlockedContext';
import { useUser } from '../contexts/UserContext';
import { launchMeetingCore } from '../services/meetingWindow/launchMeetingCore';
import { openMeetingInCurrentTab } from '../services/meetingWindow/openMeetingInCurrentTab';
import { assertUnreachable } from '../utils/assertUnreachable';
import { isTeamHubDesktop } from '../utils/desktopApp';
import { buildMeetingPath } from '../utils/url';

/**
 * Launch a meeting from a synchronous user gesture (e.g. "Join by ID").
 *
 * In hosted (MF) mode the meeting opens in a separate popup window served from
 * NG Meet's own origin (so Jitsi's `external_api.js` is present and the call is
 * isolated from the host) — enforcing a single meeting at a time. In standalone
 * mode it navigates to the in-page meeting route as before.
 *
 * The hosted branching lives in `launchMeetingCore` so the `meet` inter-module
 * API can run the identical launch without React (SM-1254). This hook adds the
 * two pieces that need React: the blocked-popup dialog and standalone
 * navigation.
 *
 * For launches that must await async work before the room id is known (e.g.
 * "New meeting", which creates the meeting first), use
 * `reserveMeetingWindow` directly so the popup opens inside the gesture.
 */
export const useLaunchMeeting = (): ((meetingId: string, password?: string | null) => void) => {
  const navigate = useNavigate();
  const { getIdToken, getAccessToken } = useUser();
  const { promptPopupBlocked } = usePopupBlocked();

  return useCallback(
    (meetingId: string, password?: string | null) => {
      // Sourced from the mounted app's user context rather than the host bridge
      // directly, so the popup gets the same identity the rest of the app uses
      // (including the popup-handoff provider when Meet is itself in a popup).
      const getAuth = async () => ({
        idToken: await getIdToken(),
        accessToken: await getAccessToken(),
      });
      const result = launchMeetingCore({ meetingId, password, getAuth });
      switch (result.status) {
        case 'launched':
          return;
        case 'existing':
          // A meeting window is already open — it has been focused for us.
          return;
        case 'confirming':
          // A different meeting is running; that window is now asking the user
          // whether to leave it for this one. Nothing to do on this page.
          return;
        case 'blocked':
          // Don't silently open in the host tab — let the user choose. The
          // fallback is a full-page navigation to NG Meet's own origin (so
          // Jitsi's external_api.js loads), NOT an in-page (memory-router)
          // navigation inside the TeamHub shell.
          promptPopupBlocked({
            meetingId,
            password,
            getAuth,
            // Never offer the desktop app a full-page navigation: it has no
            // chrome to come back with, so the TeamHub shell would be gone
            // until the user quits and relaunches.
            onOpenInPage: isTeamHubDesktop()
              ? undefined
              : () => openMeetingInCurrentTab(meetingId, password),
          });
          return;
        case 'declined':
          // Standalone: in-page navigation to the meeting route.
          navigate(buildMeetingPath(meetingId, password));
          return;
        default:
          assertUnreachable(result.status);
      }
    },
    [navigate, getIdToken, getAccessToken, promptPopupBlocked],
  );
};
